import React, { useState } from 'react';

import { Button, Divider, Input, message, Modal, Form, notification, Spin, Typography } from 'antd';
import { observer } from 'mobx-react';

import { FormErrorParser, ErrorRender } from '@partsbadger/library';

import api from '../../../utils/api';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

interface ContactProps {
    salesorder?: {
        id?: number;
        account?: {
            invoice_email_address?: string;
        };
        contact?: {
            email?: string;
            first_name?: string;
        };
    };
    updateData?: () => void;
    open: boolean;
    onClose: () => void;
}

const RequestInvoice: React.FC<ContactProps> = props => {
    const [loading, setLoading] = useState<boolean>(false);

    const [form] = Form.useForm();

    const handleClose = () => {
        props.onClose();
        form.resetFields();
    };

    const onSubmit = async (values: { notes?: string }) => {
        if (!props?.salesorder?.id) return;

        setLoading(true);
        try {
            await api.Orders.contactRequestInvoice(props.salesorder.id, {
                username: props.salesorder?.contact?.first_name,
                subject: 'Request Invoice',
                message: values.notes?.trim() ? values.notes : '-',
                email: props.salesorder?.account?.invoice_email_address,
            });
            message.success('Your request has been sent successfully');
            handleClose();
            props.updateData?.();
        } catch (error: unknown) {
            notification.error({
                message: <ErrorRender error={error as never} />,
                placement: 'topRight',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <Title level={3} className="mb-0">
                    Request Invoice
                    {props.salesorder?.id != null ? ` — Order No. ${props.salesorder.id}` : ''}
                </Title>
            }
            open={props.open}
            footer={null}
            onCancel={handleClose}
            destroyOnClose
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onSubmit}
                    onFinishFailed={errorInfo => {
                        notification.error({
                            key: 'form-errors',
                            duration: 8,
                            message: 'Please input all required fields',
                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                            placement: 'top',
                            maxCount: 1,
                        });
                    }}
                >
                    <Paragraph className="mb-0">
                        Use the form below to request an invoice for this order. Your account team will follow up using
                        your invoice email on file.
                    </Paragraph>

                    <Divider />

                    <Form.Item
                        name="notes"
                        label="Notes (optional)"
                        rules={[{ required: true, message: 'Please input a notes to request invoice' }]}
                    >
                        <TextArea
                            placeholder="Add any details that will help us process your invoice request…"
                            rows={4}
                        />
                    </Form.Item>

                    <div className="d-flex flex-column">
                        <Button className="w-100 mb-2" htmlType="submit" type="primary">
                            Submit request
                        </Button>
                        <Button onClick={handleClose} className="w-100" htmlType="button">
                            Cancel
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
};

export default observer(RequestInvoice);
